// src/features/blog/components/article-body-with-cards.tsx
import { Fragment, useMemo } from "react";
import { InlineActivityCard } from "./inline-activity-card";

interface ArticleBodyWithCardsProps {
  readonly html: string;
  readonly locale?: string;
}

interface Section {
  readonly html: string;
}

// Headings clear the sticky header (plus a breath) when jumped to, by the
// contents panel or a plain `#id` link; the margin lives inside the zoomed
// body, so it is right at the 0.9 desktop zoom too.
const PROSE_CLASSES =
  "[&_h2]:scroll-mt-[calc(var(--mj-header-h,68px)+20px)] [&_h3]:scroll-mt-[calc(var(--mj-header-h,68px)+20px)] mj-article-body font-sans text-[16.5px] leading-[1.85] text-[#3a3c54] [&_blockquote]:my-7 [&_blockquote]:rounded-2xl [&_blockquote]:border-l-4 [&_blockquote]:border-[#E5B265] [&_blockquote]:bg-white/50 [&_blockquote]:p-5 [&_blockquote]:font-heading [&_blockquote]:text-[19px] [&_blockquote]:italic [&_blockquote]:text-[#110E39] [&_blockquote]:backdrop-blur [&_h2]:mt-10 [&_h2]:font-heading [&_h2]:text-[26px] [&_h2]:font-bold [&_h2]:text-[#110E39] [&_h3]:mt-8 [&_h3]:font-heading [&_h3]:text-[19px] [&_h3]:font-bold [&_h3]:text-[#110E39] [&_img]:my-6 [&_img]:rounded-2xl [&_li]:mt-1.5 [&_p]:mt-4 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:ps-6 [&_code]:break-words [&_pre]:overflow-x-auto [&_table]:block [&_table]:overflow-x-auto";

const RESERVED_PATH_SEGMENT_RE =
  /^(blog|explore|cities|categories|category|activities|account|booking|bookings|cart|wishlist|en|fr|ar|es|it|de|nl|city|place|shows|stays|spas|pools|restaurants|locations|guides|golf)$/i;

function extractSlugsFromHtml(html: string): string[] {
  const slugs: string[] = [];
  const seen = new Set<string>();
  const linkRe = /<a[^>]*href=["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = linkRe.exec(html)) !== null) {
    const href = m[1];
    if (!href || href.startsWith("#") || href.startsWith("mailto:")) continue;
    let pathname = href;
    try {
      pathname = new URL(href, "https://x.invalid").pathname;
    } catch {
      // keep raw
    }
    const parts = pathname.split("/").filter(Boolean);
    if (parts.length === 0) continue;
    const last = parts[parts.length - 1];
    if (!last) continue;
    if (RESERVED_PATH_SEGMENT_RE.test(last)) continue;
    if (last.length < 4) continue;
    if (!/[a-z]/i.test(last)) continue;
    const slug = last.toLowerCase();
    if (seen.has(slug)) continue;
    seen.add(slug);
    slugs.push(slug);
  }
  return slugs;
}

function splitByHeading(html: string): Section[] {
  const parts = html.split(/(?=<h[23]\b)/i);
  return parts
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => ({ html: part }));
}

function findSectionSlug(sectionHtml: string): string | null {
  const slugs = extractSlugsFromHtml(sectionHtml);
  return slugs[0] ?? null;
}

const CARD_MARKER_RE = /<!--\s*card:([a-z0-9-]+)\s*-->/gi;

type ArticlePart = { type: "html"; content: string } | { type: "card"; slug: string };

function splitByCardMarkers(html: string): ArticlePart[] | null {
  CARD_MARKER_RE.lastIndex = 0;
  if (!CARD_MARKER_RE.test(html)) return null;
  CARD_MARKER_RE.lastIndex = 0;

  const parts: ArticlePart[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = CARD_MARKER_RE.exec(html)) !== null) {
    if (m.index > lastIndex) {
      parts.push({ type: "html", content: html.slice(lastIndex, m.index) });
    }
    parts.push({ type: "card", slug: m[1].toLowerCase() });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < html.length) {
    parts.push({ type: "html", content: html.slice(lastIndex) });
  }
  return parts;
}

export function ArticleBodyWithCards({ html, locale = "en" }: ArticleBodyWithCardsProps) {
  const explicitParts = useMemo(() => splitByCardMarkers(html), [html]);

  const sections = useMemo<Section[]>(() => {
    if (explicitParts) return [];
    return splitByHeading(html);
  }, [html, explicitParts]);

  if (explicitParts) {
    return (
      <div className={PROSE_CLASSES}>
        {explicitParts.map((p, i) =>
          p.type === "html" ? (
            <div key={i} dangerouslySetInnerHTML={{ __html: p.content }} />
          ) : (
            <InlineActivityCard key={i} slug={p.slug} locale={locale} />
          ),
        )}
      </div>
    );
  }

  const renderedCardSlugs = new Set<string>();

  return (
    <div className={PROSE_CLASSES}>
      {sections.map((section, i) => {
        const slug = findSectionSlug(section.html);
        const showCard = slug !== null && !renderedCardSlugs.has(slug);
        if (showCard && slug) renderedCardSlugs.add(slug);
        return (
          <Fragment key={i}>
            <div dangerouslySetInnerHTML={{ __html: section.html }} />
            {showCard && slug && <InlineActivityCard slug={slug} locale={locale} />}
          </Fragment>
        );
      })}
    </div>
  );
}
